import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ProductCard from "./ProductCard";
import InfiniteScrollSentinel from "./InfiniteScrollSentinel";
import ListFetchError from "./ListFetchError";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import RetailerPriceModeFilters from "./RetailerPriceModeFilters";
import {
  fetchActowizFeatures,
  fetchActowizProducts,
  fetchActowizTopDeals,
} from "../store/actowiz/actowizSlice";
import {
  getCatalogFilterModes,
  normalizeStoreKey,
  PLATFORM_LABELS,
  RETAIL_KEYS,
} from "../utils/catalogFilters";
import {
  getInfiniteScrollNextRequest,
  hasMoreInfiniteItems,
} from "../utils/infiniteScrollPaging";
import useProductListInfiniteScroll from "../hooks/useProductListInfiniteScroll";
import { loadHomeQuickCompareTabs, saveHomeQuickCompareTabs } from "../utils/catalogTabState";

export default function HomeQuickCompareSection({
  onAddToList,
  onOpenProduct,
}) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [retailer, setRetailer] = useState(() => loadHomeQuickCompareTabs().retailer);
  const [priceMode, setPriceMode] = useState(() => loadHomeQuickCompareTabs().priceMode);

  const features = useAppSelector((s) => s.actowiz.features);
  const featuresStatus = useAppSelector((s) => s.actowiz.featuresStatus);
  const featuresError = useAppSelector((s) => s.actowiz.featuresError);
  const featuresLastBatchSize = useAppSelector((s) => s.actowiz.featuresLastBatchSize);
  const featuresFetchedTotal = useAppSelector((s) => s.actowiz.featuresFetchedTotal);

  const topDeals = useAppSelector((s) => s.actowiz.topDeals);
  const topDealsStatus = useAppSelector((s) => s.actowiz.topDealsStatus);
  const topDealsError = useAppSelector((s) => s.actowiz.topDealsError);
  const topDealsLastBatchSize = useAppSelector((s) => s.actowiz.topDealsLastBatchSize);
  const topDealsFetchedTotal = useAppSelector((s) => s.actowiz.topDealsFetchedTotal);

  const byPlatform = useAppSelector((s) => s.actowiz.byPlatform);
  const statusByPlatform = useAppSelector((s) => s.actowiz.statusByPlatform);
  const errorByPlatform = useAppSelector((s) => s.actowiz.errorByPlatform);
  const byPlatformLastBatchSize = useAppSelector((s) => s.actowiz.byPlatformLastBatchSize);
  const byPlatformFetchedTotal = useAppSelector((s) => s.actowiz.byPlatformFetchedTotal);

  const {
    isOfferMode,
    isFeaturesCompareMode,
    isFeaturesOfferCompareMode,
    isPlatformOfferMode,
  } = getCatalogFilterModes(retailer, priceMode);
  const isMultiRetailerCompareMode = isFeaturesCompareMode || isFeaturesOfferCompareMode;

  const prevFilterKeyRef = useRef("");
  const loadingMoreRef = useRef(false);

  const filterKey = useMemo(
    () => `${retailer}|${priceMode}`,
    [retailer, priceMode]
  );

  useEffect(() => {
    saveHomeQuickCompareTabs({ retailer, priceMode });
  }, [retailer, priceMode]);

  const fetchProducts = useCallback(
    ({ apiLoadedCount = 0, isRefresh = false } = {}) => {
      const { start, offset, mode } = getInfiniteScrollNextRequest({ apiLoadedCount, isRefresh });
      const arg = { start, offset, mode, force: true };

      if (isFeaturesOfferCompareMode) {
        dispatch(fetchActowizFeatures({ ...arg, offer: true }));
        return;
      }
      if (isPlatformOfferMode) {
        dispatch(fetchActowizTopDeals({ ...arg, platform_list: [retailer] }));
        return;
      }
      if (isFeaturesCompareMode) {
        dispatch(fetchActowizFeatures(arg));
        return;
      }
      dispatch(fetchActowizProducts({ ...arg, platform: retailer }));
    },
    [
      dispatch,
      retailer,
      isFeaturesOfferCompareMode,
      isPlatformOfferMode,
      isFeaturesCompareMode,
    ]
  );

  useEffect(() => {
    if (prevFilterKeyRef.current === filterKey) return;
    prevFilterKeyRef.current = filterKey;
    loadingMoreRef.current = false;
    // Filter change refreshes products only — do not scroll to top.
    fetchProducts({ isRefresh: true });
  }, [filterKey, fetchProducts]);

  const rawItemsCount = useMemo(() => {
    if (isMultiRetailerCompareMode) return (features || []).length;
    if (isPlatformOfferMode) return (topDeals || []).length;
    return (byPlatform?.[retailer] || []).length;
  }, [isMultiRetailerCompareMode, isPlatformOfferMode, features, topDeals, byPlatform, retailer]);

  const productItems = useMemo(() => {
    if (isMultiRetailerCompareMode) {
      return (features || []).filter(
        (p) => p?.title && Array.isArray(p?.comparison) && p.comparison.length > 0
      );
    }
    if (isPlatformOfferMode) {
      return (topDeals || [])
        .filter((p) => p?.title)
        .filter((p) => normalizeStoreKey(p?.platform) === retailer);
    }
    const raw = byPlatform?.[retailer] || [];
    return (Array.isArray(raw) ? raw : []).filter((p) => p?.title);
  }, [isMultiRetailerCompareMode, isPlatformOfferMode, retailer, topDeals, features, byPlatform]);

  const status = isMultiRetailerCompareMode
    ? featuresStatus
    : isPlatformOfferMode
    ? topDealsStatus
    : statusByPlatform?.[retailer] || "idle";

  const error = isMultiRetailerCompareMode
    ? featuresError
    : isPlatformOfferMode
    ? topDealsError
    : errorByPlatform?.[retailer] || "";

  const lastBatchSize = isMultiRetailerCompareMode
    ? featuresLastBatchSize
    : isPlatformOfferMode
    ? topDealsLastBatchSize
    : byPlatformLastBatchSize?.[retailer] ?? 0;

  const fetchedTotal = isMultiRetailerCompareMode
    ? featuresFetchedTotal
    : isPlatformOfferMode
    ? topDealsFetchedTotal
    : byPlatformFetchedTotal?.[retailer] ?? 0;

  const isLoading = status === "loading";
  const isInitialLoading = isLoading && rawItemsCount === 0;
  const isLoadingMore = isLoading && rawItemsCount > 0;
  const isLoadMoreError = Boolean(error) && productItems.length > 0;
  const hasMore = hasMoreInfiniteItems({ lastBatchSize });

  const loadMore = useCallback(() => {
    if (isLoading || !hasMore || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    fetchProducts({ apiLoadedCount: fetchedTotal, isRefresh: false });
  }, [isLoading, hasMore, fetchedTotal, fetchProducts]);

  const retryFetch = useCallback(() => {
    if (isLoading) return;
    loadingMoreRef.current = false;
    if (productItems.length === 0) {
      fetchProducts({ isRefresh: true });
      return;
    }
    fetchProducts({ apiLoadedCount: fetchedTotal, isRefresh: false });
  }, [isLoading, productItems.length, fetchedTotal, fetchProducts]);

  useEffect(() => {
    if (!isLoading) loadingMoreRef.current = false;
  }, [isLoading]);

  const { bindPrefetchNode, prefetchIndex } = useProductListInfiniteScroll({
    itemCount: productItems.length,
    hasMore: hasMore && !error,
    isLoading,
    onLoadMore: loadMore,
  });

  useEffect(() => {
    if (isLoading || !hasMore || error) return;
    if (productItems.length === 0 && rawItemsCount > 0) {
      loadMore();
    }
  }, [isLoading, hasMore, error, productItems.length, rawItemsCount, loadMore]);

  const cardPricingMode = isFeaturesOfferCompareMode || isPlatformOfferMode ? "Loyalty+Offer" : "Base";

  const openProduct = (product) => {
    const productId = product?.product_id ?? product?.pid ?? product?.sku ?? "";
    if (!productId) return;
    if (typeof onOpenProduct === "function") {
      onOpenProduct(product, { retailer, priceMode });
      return;
    }
    if (isMultiRetailerCompareMode) {
      navigate(`/product/${encodeURIComponent(productId)}`, {
        state: {
          product,
          pricingMode: isOfferMode ? "Loyalty+Offer" : "Base",
          offer: isOfferMode,
        },
      });
      return;
    }
    if (isPlatformOfferMode) {
      const platformKey = normalizeStoreKey(product?.platform) || retailer;
      navigate(
        `/product-store/${encodeURIComponent(platformKey)}/${encodeURIComponent(productId)}`,
        { state: { product, pricingMode: "Loyalty+Offer", offer: true } }
      );
      return;
    }
    if (RETAIL_KEYS.has(retailer)) {
      navigate(
        `/product-store/${encodeURIComponent(retailer)}/${encodeURIComponent(productId)}`,
        { state: { product, pricingMode: "Base", offer: false } }
      );
    }
  };

  return (
    <section className="rounded-2xl bg-transparent px-3">
      <RetailerPriceModeFilters
        retailer={retailer}
        priceMode={priceMode}
        onRetailerChange={setRetailer}
        onPriceModeChange={setPriceMode}
      />

      {isInitialLoading && !error && (
        <div className="mt-8 flex justify-center">
          <span className="text-[16px] text-[#6E7B86]">Loading…</span>
        </div>
      )}

      {error && !isLoadMoreError && (
        <div className="mt-6">
          <ListFetchError message={error} onRetry={retryFetch} isLoading={isLoading} />
        </div>
      )}

      {productItems.length > 0 && (
        <div
          className={`mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3 lg:gap-8${
            isLoadingMore ? "" : isLoading ? " pointer-events-none opacity-60" : ""
          }`}
        >
          {productItems.map((product, idx) => {
            const id = product?.product_id ?? product?.pid ?? product?.sku ?? String(idx);
            const label = PLATFORM_LABELS[retailer] || "Store";
            return (
              <div
                key={`${id}-${idx}`}
                ref={idx === prefetchIndex ? bindPrefetchNode : undefined}
                className="min-w-0"
              >
                <ProductCard
                  accent={["orange", "blue", "green", "purple"][idx % 4]}
                  product={product}
                  onAddToList={(product) =>
                    onAddToList(product, {
                      detailView: isMultiRetailerCompareMode ? "compare" : "single",
                      platform: isMultiRetailerCompareMode
                        ? undefined
                        : normalizeStoreKey(product?.platform) || retailer,
                    })
                  }
                  showComparison={isMultiRetailerCompareMode}
                  retailerLabel={isMultiRetailerCompareMode ? undefined : label}
                  pricingMode={cardPricingMode}
                  onClick={() => openProduct(product)}
                />
              </div>
            );
          })}
        </div>
      )}

      {!isLoading && !error && !productItems.length && (
        <p className="mt-6 text-center text-[16px] text-[#6E7B86]">
          No products to show for these filters.
        </p>
      )}

      {(hasMore || isLoadingMore) && !isLoadMoreError && (
        <InfiniteScrollSentinel isLoading={isLoadingMore} hasMore={hasMore} />
      )}

      {isLoadMoreError && (
        <div className="mt-4">
          <ListFetchError message={error} onRetry={retryFetch} isLoading={isLoading} />
        </div>
      )}
    </section>
  );
}
